import { useTheme } from "@emotion/react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import {
  IconArrowLeft,
  IconCheck,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useCallback, useEffect } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import {
  DragAndDrop,
  InfoBanner,
  Input,
  Select,
  confirmDialog,
} from "~/components";
import { brazilStates } from "~/constants";
import { useCep, useUploadMultiFilesWithNoProposal } from "~/hooks";
import {
  addressSchema,
  documentSchema,
  fileSchema,
  phoneSchema,
} from "~/utils/yupSchema";
import * as yup from "yup";
import { useIndicationFlow } from "~/contexts";
import {
  LEGAL_REPRESENTATIVE_INITIAL_VALUES,
  LegalRepresentativeType,
  LegalRepresentativyPendency,
  documentFileIsDisabled,
  hasDuplicatedValuesInLegalRepresentativies,
  hasPendencyOrNullOnLegalRepresentativy,
} from "../utils";
import {
  EIndicationStatus,
  EProposalFileStatus,
  EProposalFileType,
  EStorageService,
  FilesUploadInputGraphqlDto,
  useCustomerUpdateLegalRepresentantMutation,
  useUpdateIndicationFilesMutation,
  useUpdateIndicationLegalRepresentativesMutation,
} from "~/graphql/types";
import { clearString } from "~/utils";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { UpdateDocWarning } from "../UpdateDocWarning";

type FormData = {
  legalRepresentativies: LegalRepresentativeType[];
};

const schema = yup
  .object({
    legalRepresentativies: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            name: yup.string().required(),
            document: documentSchema.required(),
            identificationNumber: yup.string().required(),
            email: yup.string().email().required(),
            phone: phoneSchema.required(),
            address: addressSchema,
            customerPjRepresentativeDocumentWithPhotoBack: fileSchema,
            customerPjRepresentativeDocumentWithPhotoFront: fileSchema,
          })
          .required(),
      )
      .required(),
  })
  .required();

export const LegalRepresentativies: React.FC = () => {
  const theme = useTheme();
  const {
    selectedIndication,
    selectedContract,
    selectedCustomer,
    refetchProposalAndIndication,
    goToPage,
    setSelectedCustomer,
    setSelectedContract,
  } = useIndicationFlow();
  const [uploadMultiFiles, { loading: loadingFilesUpload }] =
    useUploadMultiFilesWithNoProposal();
  const [updateIndicationFiles, { loading: updateIndicationFilesIsLoading }] =
    useUpdateIndicationFilesMutation();

  const formMethods = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const { fields, append, remove, replace } = useFieldArray({
    control: formMethods.control,
    name: "legalRepresentativies",
  });

  const { legalRepresentativies: legalRepresentativiesWatch } = useWatch({
    control: formMethods.control,
  });
  const { isLoading: fetchCepIsLoading, fetchCep } = useCep({
    showErrorMessage: true,
  });

  let hasEqualField = false;

  legalRepresentativiesWatch?.forEach((representativy, i) => {
    if (
      clearString(representativy?.document || "").length > 0 &&
      legalRepresentativiesWatch.some(
        (item, index) =>
          clearString(item?.document || "") ===
            clearString(representativy?.document || "") && i !== index,
      )
    ) {
      hasEqualField = true;
    }

    if (
      representativy.email &&
      representativy?.email?.length > 0 &&
      legalRepresentativiesWatch.some(
        (item, index) => item.email === representativy.email && i !== index,
      )
    ) {
      hasEqualField = true;
    }

    if (
      clearString(representativy.phone || "").length !== 0 &&
      legalRepresentativiesWatch.some(
        (item, index) =>
          clearString(item.phone || "") ===
            clearString(representativy.phone || "") && i !== index,
      )
    ) {
      hasEqualField = true;
    }

    if (
      representativy.identificationNumber &&
      representativy.identificationNumber.length !== 0 &&
      legalRepresentativiesWatch.some(
        (item, index) =>
          item.identificationNumber === representativy.identificationNumber &&
          i !== index,
      )
    ) {
      hasEqualField = true;
    }
  });

  const allowedStatusToUpdate = [
    EIndicationStatus.requestedProposal,
    EIndicationStatus.contractWithPendencies,
  ];
  const isStatusAllowed = allowedStatusToUpdate.includes(
    selectedIndication?.status as EIndicationStatus,
  );

  const enableSubmit =
    isStatusAllowed &&
    (legalRepresentativiesWatch?.length !==
      selectedCustomer?.legalRepresentatives?.length ||
    legalRepresentativiesWatch?.some(
      (item) =>
        (item.customerPjRepresentativeDocumentWithPhotoBack &&
          item.customerPjRepresentativeDocumentWithPhotoBack?.length > 0) ||
        (item.customerPjRepresentativeDocumentWithPhotoFront &&
          item.customerPjRepresentativeDocumentWithPhotoFront?.length > 0) ||
        Boolean(
          item.pendencies?.name?.rejectedAt ||
          item.pendencies?.document?.rejectedAt ||
          item.pendencies?.email?.rejectedAt ||
          item.pendencies?.phone?.rejectedAt ||
          item.pendencies?.zipCode?.rejectedAt ||
          item.pendencies?.state?.rejectedAt ||
          item.pendencies?.city?.rejectedAt ||
          item.pendencies?.neighborhood?.rejectedAt ||
          item.pendencies?.number?.rejectedAt,
        ),
    ));

  const hasDuplicatedData = hasDuplicatedValuesInLegalRepresentativies(
    legalRepresentativiesWatch || [],
  );

  const [
    updateLegalRepresentativies,
    { loading: updateLegalRepresentativiesIsLoading },
  ] = useUpdateIndicationLegalRepresentativesMutation({
    async onCompleted(data) {
      if (data.updateIndicationLegalRepresentatives) {
        // await new Promise(resolve => setTimeout(resolve, 5000))
        await refetchProposalAndIndication();
        formMethods.clearErrors();
        toast.success("Os dados do representante legal foram salvos!");
      }
    },
    onError(error) {
      toast.error(error.message);
    },
  });

  const onSearchCep = useCallback(
    async (index: number) => {
      if (legalRepresentativiesWatch) {
        const selectedRepresentativy = legalRepresentativiesWatch[index];
        if (selectedRepresentativy) {
          const { data, isSuccess } = await fetchCep(
            selectedRepresentativy.address?.zipcode || "",
          );
          if (isSuccess) {
            formMethods.setValue(
              `legalRepresentativies.${index}.address.state`,
              data?.uf || "",
            );
            formMethods.setValue(
              `legalRepresentativies.${index}.address.city`,
              data?.localidade || "",
            );
            formMethods.setValue(
              `legalRepresentativies.${index}.address.neighborhood`,
              data?.bairro || "",
            );
            formMethods.setValue(
              `legalRepresentativies.${index}.address.street`,
              data?.logradouro || "",
            );
          }
        }
      }
    },
    [legalRepresentativiesWatch],
  );

  const contractFileRejected = (
    documentCpf: string | null | undefined,
    type: "front" | "back",
  ) => {
    const targetTypeProposal =
      type === "front"
        ? EProposalFileType.customerPjRepresentativeDocumentWithPhotoFront
        : EProposalFileType.customerPjRepresentativeDocumentWithPhotoBack;
    const contractRep =
      selectedContract?.customerRef?.legalRepresentatives?.find(
        (r) => clearString(r.document || "") === clearString(documentCpf || ""),
      );
    const file = contractRep?.files?.find(
      (item: any) => item?.type === targetTypeProposal,
    );
    if (!file) return false;
    if ("rejectedAt" in file && file.rejectedAt) return true;
    if ("status" in file && file.status === EProposalFileStatus.rejected)
      return true;
    return false;
  };

  useEffect(() => {
    const contractReps =
      selectedContract?.customerRef?.legalRepresentatives || [];
    const customerReps = selectedCustomer?.legalRepresentatives || [];
    const base = contractReps.length > 0 ? contractReps : customerReps;
    if (base && base.length > 0) {
      const merged = base.map((rep: any) => {
        const customerRep = customerReps.find(
          (r) =>
            clearString(r.document || "") === clearString(rep.document || ""),
        );
        const contractRep = contractReps.find(
          (r) =>
            clearString(r.document || "") === clearString(rep.document || ""),
        );
        return {
          ...rep,
          address: rep.address ?? customerRep?.address ?? (rep.address || {}),
          files: contractRep?.files ?? [],
          pendencies: rep.pendencies ?? null,
          identificationNumber: customerRep?.identificationNumber ?? null,
        };
      });
      replace((merged as any) || []);
      const fieldsToSetError: LegalRepresentativyPendency[] = [
        "city",
        "neighborhood",
        "number",
        "state",
        "zipCode",
        "name",
        "document",
        "email",
        "phone",
      ];

      selectedCustomer?.legalRepresentatives?.map((representativy, i) => {
        const customerPjRepresentativeDocumentWithPhotoFront =
          contractFileRejected(representativy.document, "front");
        const customerPjRepresentativeDocumentWithPhotoBack =
          contractFileRejected(representativy.document, "back");
        const contractRep =
          selectedContract?.customerRef?.legalRepresentatives?.find(
            (r) =>
              clearString(r.document || "") ===
              clearString(representativy.document || ""),
          );

        if (customerPjRepresentativeDocumentWithPhotoFront) {
          formMethods.setError(
            `legalRepresentativies.${i}.customerPjRepresentativeDocumentWithPhotoFront`,
            { message: "Este campo foi reprovado por um analista!" },
          );
        }

        if (customerPjRepresentativeDocumentWithPhotoBack) {
          formMethods.setError(
            `legalRepresentativies.${i}.customerPjRepresentativeDocumentWithPhotoBack`,
            { message: "Este campo foi reprovado por um analista!" },
          );
        }

        fieldsToSetError.forEach((item) => {
          // Se o campo existe nas pendências e tem rejeição
          if (contractRep?.pendencies && item in contractRep.pendencies) {
            const pendencyField = item as keyof typeof contractRep.pendencies;
            const pendency = contractRep.pendencies[pendencyField];

            if (pendency?.rejectedAt) {
              // Campos de endereço precisam de tratamento especial
              const addressFields = [
                "city",
                "state",
                "neighborhood",
                "number",
                "zipcode",
              ];
              if (addressFields.includes(item)) {
                formMethods.setError(
                  `legalRepresentativies.${i}.address.${item}` as any,
                  {
                    message: "Este campo foi reprovado por um analista!",
                  },
                );
              } else {
                formMethods.setError(
                  `legalRepresentativies.${i}.${item}` as any,
                  {
                    message: "Este campo foi reprovado por um analista!",
                  },
                );
              }
            }
          }
        });
      });
    } else {
      replace([LEGAL_REPRESENTATIVE_INITIAL_VALUES] as any);
    }
  }, [selectedCustomer, selectedContract]);

  useEffect(() => {
    refetchProposalAndIndication();
  }, []);

  const handleSubmit = useCallback(
    async (data: FormData) => {
      await updateLegalRepresentativies({
        variables: {
          params: {
            id: selectedIndication?._id || "",
            legalRepresentatives: data.legalRepresentativies.map(
              (representativy) => {
                return {
                  document: clearString(representativy.document),
                  email: representativy.email,
                  name: representativy.name,
                  phone: clearString(representativy.phone),
                  identificationNumber: representativy.identificationNumber,
                  address: {
                    country: "Brasil",
                    zipcode: clearString(representativy.address.zipcode),
                    city: representativy.address.city,
                    neighborhood: representativy.address.neighborhood,
                    number: representativy.address.number,
                    state: representativy.address.state,
                    street: representativy.address.street,
                    complement: representativy.address.complement,
                  },
                };
              },
            ),
          },
        },
      });

      formMethods.reset({ legalRepresentativies: data.legalRepresentativies });

      if (selectedContract) {
        const existingReps =
          selectedContract.customerRef?.legalRepresentatives || [];
        const updatedLegalRepresentativies = existingReps.map(
          (existing: any) => {
            const input = (data.legalRepresentativies || []).find(
              (r) =>
                clearString(r.document || "") ===
                clearString(existing.document || ""),
            );
            return input
              ? { ...existing, ...input, files: existing?.files || [] }
              : existing;
          },
        );
        setSelectedContract({
          ...selectedContract,
          customerRef: {
            ...selectedContract.customerRef,
            legalRepresentatives: updatedLegalRepresentativies,
          },
        } as any);
      }

      if (data.legalRepresentativies && data.legalRepresentativies.length > 0) {
        for (
          let index = 0;
          index < data.legalRepresentativies.length;
          index++
        ) {
          const selectedRepresentativy = data.legalRepresentativies[index];
          const filesToUpload: File[][] = [];
          const fileTypes: EProposalFileType[] = [];
          if (
            selectedRepresentativy.customerPjRepresentativeDocumentWithPhotoBack &&
            selectedRepresentativy.customerPjRepresentativeDocumentWithPhotoBack
              .length > 0
          ) {
            filesToUpload.push(
              selectedRepresentativy.customerPjRepresentativeDocumentWithPhotoBack,
            );
            fileTypes.push(
              EProposalFileType.customerPjRepresentativeDocumentWithPhotoBack,
            );
          }
          if (
            selectedRepresentativy.customerPjRepresentativeDocumentWithPhotoFront &&
            selectedRepresentativy
              .customerPjRepresentativeDocumentWithPhotoFront.length > 0
          ) {
            filesToUpload.push(
              selectedRepresentativy.customerPjRepresentativeDocumentWithPhotoFront,
            );
            fileTypes.push(
              EProposalFileType.customerPjRepresentativeDocumentWithPhotoFront,
            );
          }
          let uploadedFiles = null as any;
          if (filesToUpload.length > 0 && fileTypes.length > 0) {
            uploadedFiles = await uploadMultiFiles({
              types: fileTypes,
              files: filesToUpload,
            });
          }
          const typesWithNew = new Set<EProposalFileType>(fileTypes);
          const existingFiles = selectedRepresentativy.files || [];
          const filesToSend: {
            type: EProposalFileType;
            file: {
              fileName: string;
              fileUrl: string;
              key: string;
              storageService: EStorageService;
            };
          }[] = [];
          if (uploadedFiles && uploadedFiles.length > 0) {
            for (let ufIndex = 0; ufIndex < uploadedFiles.length; ufIndex++) {
              filesToSend.push({
                type: uploadedFiles[ufIndex].type as EProposalFileType,
                file: {
                  fileName: uploadedFiles[ufIndex].file.fileName,
                  fileUrl: uploadedFiles[ufIndex].file.fileUrl,
                  key: uploadedFiles[ufIndex].file.key,
                  storageService: uploadedFiles[ufIndex].file
                    .storageService as EStorageService,
                },
              });
            }
          }
          for (let fIndex = 0; fIndex < existingFiles.length; fIndex++) {
            const f = existingFiles[fIndex];
            const fType = f?.type as EProposalFileType;
            if (!typesWithNew.has(fType)) {
              const it = f?.file;
              filesToSend.push({
                type: fType,
                file: {
                  fileName: it?.fileName || "",
                  fileUrl: it?.fileUrl || "",
                  key: it?.key || "",
                  storageService:
                    (it?.storageService as EStorageService) ||
                    EStorageService.awsS3,
                },
              });
            }
          }

          const nextFilesImmediate = existingFiles.map((f: any) => {
            const repl = filesToSend.find(
              (fs) => fs.type === (f?.type as EProposalFileType),
            );
            if (repl) {
              return {
                ...f,
                type: repl.type,
                status: EProposalFileStatus.pending,
                rejectedAt: null,
                sentAt: new Date().toISOString(),
                file: {
                  fileName: repl.file.fileName,
                  fileUrl: repl.file.fileUrl,
                  key: repl.file.key,
                  storageService: repl.file.storageService,
                },
              };
            }
            return f;
          });
          filesToSend.forEach((fs) => {
            if (
              !nextFilesImmediate.some(
                (f: any) => (f?.type as EProposalFileType) === fs.type,
              )
            ) {
              nextFilesImmediate.push({
                type: fs.type,
                status: EProposalFileStatus.pending,
                rejectedAt: null,
                sentAt: new Date().toISOString(),
                file: {
                  fileName: fs.file.fileName,
                  fileUrl: fs.file.fileUrl,
                  key: fs.file.key,
                  storageService: fs.file.storageService,
                },
              });
            }
          });
          const repsBefore = formMethods.getValues(
            "legalRepresentativies",
          ) as any[];
          const repsAfter = repsBefore.map((r, ri) =>
            ri === index ? { ...r, files: nextFilesImmediate } : r,
          );
          replace(repsAfter as any);
          if (selectedContract) {
            const updatedContractRepsImmediate = (
              selectedContract.customerRef?.legalRepresentatives || []
            ).map((rep: any) => {
              if (
                clearString(rep.document || "") ===
                clearString(selectedRepresentativy.document || "")
              ) {
                return { ...rep, files: nextFilesImmediate };
              }
              return rep;
            });
            setSelectedContract({
              ...selectedContract,
              customerRef: {
                ...selectedContract.customerRef,
                legalRepresentatives: updatedContractRepsImmediate,
              },
            } as any);
          }
          for (let sIndex = 0; sIndex < filesToSend.length; sIndex++) {
            await updateIndicationFiles({
              variables: {
                params: {
                  id: selectedIndication?._id || "",
                  type: filesToSend[sIndex].type,
                  legalRepresentativeDocument: clearString(
                    selectedRepresentativy.document,
                  ),
                  file: filesToSend[sIndex].file,
                },
              },
              async onCompleted(completedData) {
                if (
                  index === data.legalRepresentativies.length - 1 &&
                  sIndex === filesToSend.length - 1
                ) {
                  await new Promise((resolve) => setTimeout(resolve, 5000));
                  await refetchProposalAndIndication();
                  formMethods.clearErrors();
                  toast.success(
                    "Os documentos do representante legal foram salvos!",
                  );
                  goToPage(3);
                }
              },
              onError(error) {
                toast.error(error.message);
              },
            });
          }
        }
      }
    },
    [selectedIndication, selectedContract],
  );

  const onRemove = useCallback(
    async (index: number) => {
      if (legalRepresentativiesWatch) {
        const isConfirmed = await confirmDialog({
          title: "Remoção de representante legal",
          type: "warning",
          content: (
            <Box>
              <p>
                Esta ação irá remover o representante legal{" "}
                <b>{`${legalRepresentativiesWatch[index].name}`}</b> da
                proposta.
              </p>
              <p>
                Para salvar a informação de exclusão de um representante legal,
                você deverá clicar em {'"Confirmar"'} no rodapé da página após
                esta ação.
              </p>
            </Box>
          ),
        });

        if (isConfirmed) {
          remove(index);
        }
      }
    },
    [fields, legalRepresentativiesWatch],
  );

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit((data) => handleSubmit(data))}>
          {fields.map((field, index) => {
            const customerPjRepresentativeDocumentWithPhotoFront =
              field?.files?.find(
                (item) =>
                  item?.type ===
                  EProposalFileType.customerPjRepresentativeDocumentWithPhotoFront,
              );
            const customerPjRepresentativeDocumentWithPhotoBack =
              field?.files?.find(
                (item) =>
                  item?.type ===
                  EProposalFileType.customerPjRepresentativeDocumentWithPhotoBack,
              );

            const getStatus = (file: any): string | null =>
              file?.status ?? null;
            const getSentAt = (file: any): string | null =>
              "sentAt" in (file || {})
                ? (file?.sentAt ?? null)
                : (file?.sendAt ?? null);

            const canDelete =
              field?.pendencies?.name.acceptedAt &&
              field?.pendencies?.email.acceptedAt &&
              field?.pendencies?.phone.acceptedAt &&
              field?.pendencies?.zipCode.acceptedAt &&
              field?.pendencies?.state.acceptedAt &&
              field?.pendencies?.city.acceptedAt &&
              field?.pendencies?.neighborhood.acceptedAt &&
              field?.pendencies?.number.acceptedAt &&
              getStatus(customerPjRepresentativeDocumentWithPhotoFront) ===
                EProposalFileStatus.accepted &&
              getStatus(customerPjRepresentativeDocumentWithPhotoBack) ===
                EProposalFileStatus.accepted;

            return (
              <Box key={field.id}>
                {fields.length > 1 && <Divider sx={{ margin: "2rem 0" }} />}

                <Typography
                  sx={{ paddingBottom: "1rem" }}
                  variant="h3"
                  fontWeight={500}
                  color={theme.palette.grey[800]}
                >
                  Representante legal
                </Typography>
                <Grid sx={{ paddingBottom: "1rem" }} container spacing={3}>
                  <Grid xs={12} md={8} item>
                    <Input
                      disabled={hasPendencyOrNullOnLegalRepresentativy(
                        "name",
                        field?.pendencies,
                      )}
                      label="Nome"
                      name={`legalRepresentativies.${index}.name`}
                    />
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <Input
                      disabled={hasPendencyOrNullOnLegalRepresentativy(
                        "document",
                        field?.pendencies,
                      )}
                      mask="cpf"
                      label="CPF"
                      name={`legalRepresentativies.${index}.document`}
                    />
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <Input
                      disabled={true}
                      label="RG"
                      name={`legalRepresentativies.${index}.identificationNumber`}
                    />
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <Input
                      disabled={hasPendencyOrNullOnLegalRepresentativy(
                        "email",
                        field?.pendencies,
                      )}
                      label="Email"
                      name={`legalRepresentativies.${index}.email`}
                    />
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <Input
                      disabled={hasPendencyOrNullOnLegalRepresentativy(
                        "phone",
                        field?.pendencies,
                      )}
                      mask="phone"
                      label="Telefone"
                      name={`legalRepresentativies.${index}.phone`}
                    />
                  </Grid>
                </Grid>

                <Typography
                  sx={{ paddingBottom: "1rem" }}
                  variant="h5"
                  fontWeight={500}
                  color={theme.palette.grey[800]}
                >
                  Endereço
                </Typography>
                <Grid container spacing={3}>
                  <Grid xs={12} md={4} item>
                    <Input
                      disabled={hasPendencyOrNullOnLegalRepresentativy(
                        "zipCode",
                        field?.pendencies,
                      )}
                      icons={{
                        end: {
                          element: fetchCepIsLoading ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <IconButton
                              disabled={hasPendencyOrNullOnLegalRepresentativy(
                                "zipCode",
                                field?.pendencies,
                              )}
                              onClick={() => onSearchCep(index)}
                            >
                              <IconSearch />
                            </IconButton>
                          ),
                        },
                      }}
                      mask="cep"
                      name={`legalRepresentativies.${index}.address.zipcode`}
                      label="CEP"
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ paddingTop: "0 !important" }}
                  ></Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{ paddingTop: "0 !important" }}
                  ></Grid>

                  <Grid item xs={12} md={4}>
                    <Input
                      disabled={true}
                      name={`legalRepresentativies.${index}.address.street`}
                      label="Rua"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Input
                      disabled={hasPendencyOrNullOnLegalRepresentativy(
                        "number",
                        field?.pendencies,
                      )}
                      name={`legalRepresentativies.${index}.address.number`}
                      label="Número"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Input
                      disabled={true}
                      name={`legalRepresentativies.${index}.address.complement`}
                      label="Complemento (Opcional)"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Input
                      disabled={hasPendencyOrNullOnLegalRepresentativy(
                        "neighborhood",
                        field?.pendencies,
                      )}
                      name={`legalRepresentativies.${index}.address.neighborhood`}
                      label="Bairro"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Input
                      disabled={hasPendencyOrNullOnLegalRepresentativy(
                        "city",
                        field?.pendencies,
                      )}
                      name={`legalRepresentativies.${index}.address.city`}
                      label="Cidade"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Select
                      disabled={hasPendencyOrNullOnLegalRepresentativy(
                        "state",
                        field?.pendencies,
                      )}
                      name={`legalRepresentativies.${index}.address.state`}
                      label="UF"
                      options={brazilStates}
                    />
                  </Grid>
                </Grid>

                <Typography
                  sx={{ padding: "1.5rem 0" }}
                  variant="h5"
                  fontWeight={500}
                  color={theme.palette.grey[800]}
                >
                  Documento
                </Typography>
                <UpdateDocWarning />
                <Grid container spacing={3} marginTop={1.5}>
                  <Grid item xs={12} lg={6}>
                    <DragAndDrop
                      fileDescription="(JPG, JPEG, PNG, PDF. Tamanho máximo 1MB)"
                      name={`legalRepresentativies.${index}.customerPjRepresentativeDocumentWithPhotoFront`}
                      label="Anexar RG ou CNH (Frente)"
                      $variant="secondary"
                      disabled={documentFileIsDisabled(
                        customerPjRepresentativeDocumentWithPhotoFront as any,
                      )}
                      status={getStatus(
                        customerPjRepresentativeDocumentWithPhotoFront,
                      )}
                      sentAt={getSentAt(
                        customerPjRepresentativeDocumentWithPhotoFront,
                      )}
                      $completed={Boolean(
                        getSentAt(
                          customerPjRepresentativeDocumentWithPhotoFront,
                        ),
                      )}
                      fileName={
                        customerPjRepresentativeDocumentWithPhotoFront?.file
                          ?.fileName
                      }
                      fileUrl={
                        customerPjRepresentativeDocumentWithPhotoFront?.file
                          ?.fileUrl
                      }
                      accepted={{
                        images: true,
                        pdf: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <DragAndDrop
                      fileDescription="(JPG, JPEG, PNG, PDF. Tamanho máximo 1MB)"
                      name={`legalRepresentativies.${index}.customerPjRepresentativeDocumentWithPhotoBack`}
                      label="Anexar RG ou CNH (Verso)"
                      $variant="secondary"
                      disabled={documentFileIsDisabled(
                        customerPjRepresentativeDocumentWithPhotoBack as any,
                      )}
                      status={getStatus(
                        customerPjRepresentativeDocumentWithPhotoBack,
                      )}
                      sentAt={getSentAt(
                        customerPjRepresentativeDocumentWithPhotoBack,
                      )}
                      $completed={Boolean(
                        getSentAt(
                          customerPjRepresentativeDocumentWithPhotoBack,
                        ),
                      )}
                      fileName={
                        customerPjRepresentativeDocumentWithPhotoBack?.file
                          ?.fileName
                      }
                      fileUrl={
                        customerPjRepresentativeDocumentWithPhotoBack?.file
                          ?.fileUrl
                      }
                      accepted={{
                        images: true,
                        pdf: true,
                      }}
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    marginTop: "2rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {fields.length - 1 === index ? (
                    <Button
                      size="small"
                      color="secondary"
                      startIcon={<IconPlus />}
                      disabled={true}
                      onClick={() =>
                        append(LEGAL_REPRESENTATIVE_INITIAL_VALUES)
                      }
                    >
                      Adicionar representante
                    </Button>
                  ) : (
                    <div></div>
                  )}
                  {fields?.length > 1 && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<IconTrash />}
                      disabled={true}
                      onClick={() => onRemove(index)}
                    >
                      Remover representante
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}

          <Divider sx={{ margin: "2rem 0" }} />

          {hasEqualField && (
            <Box sx={{ paddingBottom: "2rem" }}>
              <InfoBanner
                text="Os campos (RG, CPF, E-mail e Telefone) não podem repetir entre os representantes"
                type="warning"
              />
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Link to="/app/indications">
              <Button startIcon={<IconArrowLeft />} color="secondary">
                Voltar
              </Button>
            </Link>
            {hasDuplicatedData ? (
              <Button disabled={true} type="submit">
                Confirmar
              </Button>
            ) : (
              <Button
                disabled={
                  updateLegalRepresentativiesIsLoading ||
                  loadingFilesUpload ||
                  updateIndicationFilesIsLoading ||
                  !enableSubmit ||
                  hasEqualField
                }
                type="submit"
                startIcon={
                  updateLegalRepresentativiesIsLoading ||
                  loadingFilesUpload ||
                  updateIndicationFilesIsLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <IconCheck size={24} />
                  )
                }
              >
                Confirmar
              </Button>
            )}
          </Box>
        </form>
      </FormProvider>
    </>
  );
};
